import { useRef } from "react"
import PropTypes from "prop-types"
import ReactSelect from "react-select"
import AsyncSelect from "react-select/async"
import { cn } from "../../../lib/utils"
import { buildReactSelectStyles } from "../_reactSelectStyles"

/**
 * AppCombobox — Radix-aligned combobox primitive.
 *
 * Uses react-select under the hood, themed to match Radix Themes (accentColor="slate").
 *
 * ─── Modes ──────────────────────────────────────────────────────────────
 *
 * **Sync (default)** — Pass `options` array. Client-side filtering via type-to-search.
 *   <AppCombobox options={[{ value: 'a', label: 'Alpha' }]} ... />
 *
 * **Async** — Pass `loadOptions` function. Server-side search with built-in debounce.
 *   <AppCombobox async loadOptions={(input) => fetch(`/api?q=${input}`).then(r => r.json())} ... />
 *
 * ─── Props ──────────────────────────────────────────────────────────────
 *
 * @param {boolean}  async             — enable async mode (uses react-select/async)
 * @param {Array}    options           — sync mode: array of { value, label }
 * @param {Function} loadOptions       — async mode: (inputValue) => Promise<{ value, label }[]>
 * @param {number}   debounceMs        — async mode: debounce delay in ms (default 400)
 * @param {object|null} value          — current selection: { value, label } or null
 * @param {Function} onChange          — (option: { value, label } | null) => void
 * @param {Function} onInputChange     — (inputValue: string, meta) => void — fires on every keystroke
 * @param {string}   placeholder       — placeholder text
 * @param {boolean}  disabled          — disables the control
 * @param {boolean}  isLoading         — show loading spinner
 * @param {boolean}  isClearable       — show clear button (default true)
 * @param {boolean}  isSearchable      — allow typing to filter (default true)
 * @param {string}   noOptionsMessage  — shown when no options match
 * @param {string}   loadingMessage    — shown while async is loading
 * @param {string}   variant           — "field" (default bordered) | "drop-field" (bottom-border only)
 * @param {string}   className         — additional CSS class on wrapper
 * @param {string}   id                — html id for the input (accessibility/label linking)
 */
export default function AppCombobox({
  // mode
  async: isAsync = false,
  // data
  options = [],
  loadOptions,
  debounceMs = 400,
  // selection
  value = null,
  onChange,
  onInputChange,
  // display
  placeholder = "Search...",
  disabled = false,
  isLoading = false,
  isClearable = true,
  isSearchable = true,
  noOptionsMessage = "No options",
  loadingMessage = "Loading...",
  // styling
  variant,
  className = "",
  // html
  id,
  "data-testid": dataTestId,
  ...rest
}) {
  // BUG FIX: useRef persists across renders so clearTimeout actually cancels the previous timer
  const debounceTimerRef = useRef(null)

  const isDropField = variant === "drop-field"

  // className applied to the react-select root element (not the wrapper div)
  const selectClassName = cn(
    "app-combobox",
    isDropField && "app-combobox--drop-field",
    className,
  ).trim()

  const sharedProps = {
    classNamePrefix: "app-combobox",
    className: selectClassName,
    inputId: id,
    value,
    onChange,
    onInputChange,
    placeholder,
    isDisabled: disabled,
    isLoading,
    isClearable,
    isSearchable,
    noOptionsMessage: () => noOptionsMessage,
    loadingMessage: () => loadingMessage,
    isMulti: false,
    menuPortalTarget: typeof document === "undefined" ? null : document.body,
    menuPosition: "fixed",
    styles: buildReactSelectStyles({ isMulti: false, variant }),
    "aria-label": rest["aria-label"],
  }

  // Wrap data-testid onto the outer container
  const wrapperTestId = dataTestId ? { "data-testid": dataTestId } : {}

  if (isAsync) {
    // Debounced async search
    const debouncedLoadOptions = (inputValue, callback) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        if (loadOptions) {
          const result = loadOptions(inputValue)
          if (result && typeof result.then === "function") {
            result.then(callback)
          } else {
            callback(result || [])
          }
        } else {
          callback([])
        }
      }, debounceMs)
    }

    return (
      <div {...wrapperTestId}>
        <AsyncSelect
          {...sharedProps}
          loadOptions={debouncedLoadOptions}
          defaultOptions={options.length > 0 ? options : true}
          cacheOptions
        />
      </div>
    )
  }

  return (
    <div {...wrapperTestId}>
      <ReactSelect {...sharedProps} options={options} />
    </div>
  )
}

AppCombobox.propTypes = {
  // mode
  async: PropTypes.bool,
  // data
  options: PropTypes.array,
  loadOptions: PropTypes.func,
  debounceMs: PropTypes.number,
  // selection
  value: PropTypes.object,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  // display
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  noOptionsMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
  // styling
  variant: PropTypes.string,
  className: PropTypes.string,
  // html
  id: PropTypes.string,
  "data-testid": PropTypes.string,
}
